import { Check, ChevronLeft, Plus, Share2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eyebrow, Wordmark } from "@/components/editorial";
import { useMenuPlan } from "@/hooks/useMenuPlan";
import { useNextWeekMenuPlan } from "@/hooks/useNextWeekMenuPlan";
import { shoppingKey, useMenu } from "@/features/menu/store";
import { AISLE_LABELS, formatPrice } from "@/features/menu/menuLabels";
import { cn } from "@/lib/utils";
import { shareMenu } from "@/lib/share";
import { toast } from "sonner";
import type { Aisle, ShoppingItem } from "@/types/menu";

export function ShoppingListPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isNextWeek = searchParams.get("week") === "next";
  const { plan: currentPlan, isLoading: isCurrentLoading } = useMenuPlan();
  const {
    plan: nextWeekPlan,
    menuId: nextWeekMenuId,
    isLoading: isNextLoading,
  } = useNextWeekMenuPlan();
  const effectivePlan = isNextWeek && nextWeekPlan ? nextWeekPlan : currentPlan;
  const isLoading = isNextWeek ? isNextLoading : isCurrentLoading;
  const picked = useMenu((s) => s.pickedItems);
  const togglePicked = useMenu((s) => s.togglePicked);

  const sortedAisles: Aisle[] = (() => {
    if (!effectivePlan?.shopping_list) return [];
    return (Object.keys(effectivePlan.shopping_list.items_by_aisle) as Aisle[])
      .filter(
        (a) =>
          (effectivePlan.shopping_list?.items_by_aisle[a]?.length ?? 0) > 0,
      )
      .sort(
        (a, b) =>
          (AISLE_LABELS[a]?.order ?? 999) - (AISLE_LABELS[b]?.order ?? 999),
      );
  })();

  const allItems: { aisle: Aisle; item: ShoppingItem }[] = sortedAisles.flatMap(
    (a) =>
      (effectivePlan!.shopping_list!.items_by_aisle[a] ?? []).map((item) => ({
        aisle: a,
        item,
      })),
  );
  const totalCount = allItems.length;
  const doneCount = allItems.filter(({ aisle, item }) =>
    picked.includes(shoppingKey(aisle, item.name)),
  ).length;
  const totalCost = effectivePlan?.estimated_cost_eur ?? 0;
  const progress = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  return (
    <div className="flex h-full flex-col bg-background pb-2">
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-4">
        <Link
          to="/dashboard"
          aria-label="Retour"
          className="flex h-9 w-9 items-center justify-center text-foreground"
        >
          <ChevronLeft className="size-5" strokeWidth={1.25} />
        </Link>
        <Wordmark size="sm" />
        <div className="flex items-center gap-2">
          <button
            aria-label="Partager la liste"
            onClick={() => {
              const mid = isNextWeek
                ? nextWeekMenuId
                : useMenu.getState().menuId;
              if (!mid) {
                toast.error("Validez d'abord votre menu avant de partager.");
                return;
              }
              void shareMenu(mid, "shopping_list");
            }}
            className="flex h-9 w-9 items-center justify-center text-foreground"
          >
            <Share2 className="size-[18px]" strokeWidth={1.25} />
          </button>
          <button
            aria-label="Ajouter un article"
            className="flex h-9 w-9 items-center justify-center text-foreground"
          >
            <Plus className="size-[18px]" strokeWidth={1.25} />
          </button>
        </div>
      </header>

      {/* Hero header */}
      <section className="px-6 pb-5 pt-3">
        <Eyebrow className="mb-1.5">
          {isNextWeek ? t("shopping.nextWeek") : "Liste de courses · S.19"}
        </Eyebrow>
        <h1 className="h-display text-[32px] leading-[1]">
          <span className="italic">{totalCount}</span> ingrédients,
          <br />
          rangés par allée.
        </h1>

        <div className="mt-5">
          <div className="mb-1.5 flex justify-between text-[11px] text-ink-50">
            <span>
              {doneCount} / {totalCount} cochés
            </span>
            <span className="font-mono">~ {formatPrice(totalCost)}</span>
          </div>
          <div className="h-[3px] overflow-hidden rounded-full bg-bone">
            <div
              className="h-full bg-accent transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </section>

      {/* Aisles */}
      <div className="flex-1 overflow-auto px-5 pb-6">
        {isLoading && (
          <p className="py-12 text-center text-[13px] text-ink-50">
            Chargement…
          </p>
        )}
        {!isLoading && !effectivePlan?.shopping_list && (
          <p className="py-12 text-center text-[13px] text-ink-50">
            Aucune liste de courses pour cette semaine.
          </p>
        )}
        {sortedAisles.map((aisle, idx) => {
          const items =
            effectivePlan!.shopping_list!.items_by_aisle[aisle] ?? [];
          const localDone = items.filter((i) =>
            picked.includes(shoppingKey(aisle, i.name)),
          ).length;
          return (
            <AisleSection
              key={aisle}
              index={idx + 1}
              aisle={aisle}
              items={items}
              picked={picked}
              done={localDone}
              onToggle={(item) => togglePicked(shoppingKey(aisle, item.name))}
            />
          );
        })}
      </div>
    </div>
  );
}

function AisleSection({
  aisle,
  items,
  picked,
  done,
  index,
  onToggle,
}: {
  aisle: Aisle;
  items: ShoppingItem[];
  picked: string[];
  done: number;
  index: number;
  onToggle: (item: ShoppingItem) => void;
}) {
  const label = AISLE_LABELS[aisle] ?? AISLE_LABELS["other"];
  return (
    <section className="mb-5 overflow-hidden border border-hair bg-background">
      <header className="flex items-center justify-between border-b border-hair bg-paper px-5 py-3.5">
        <div>
          <h2 className="font-serif text-[18px] italic tracking-[-0.005em]">
            <span className="mr-1.5 not-italic">{label.emoji}</span>
            {label.fr}
          </h2>
          <div className="mt-0.5 text-[11px] text-ink-50">
            {done} / {items.length} récupérés
          </div>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-hair bg-background font-serif text-[16px] font-medium">
          {index}
        </div>
      </header>
      <ul className="divide-y divide-hair">
        {items.map((item) => {
          const key = shoppingKey(aisle, item.name);
          const isPicked = picked.includes(key);
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onToggle(item)}
                className={cn(
                  "flex w-full items-center gap-3.5 px-5 py-3 text-left transition-opacity",
                  isPicked && "opacity-50",
                )}
                aria-pressed={isPicked}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                    isPicked
                      ? "border-accent bg-accent text-background"
                      : "border-ink-30",
                  )}
                >
                  {isPicked && <Check className="size-3" strokeWidth={2.5} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className={cn(
                      "font-serif text-[16px] tracking-[-0.005em]",
                      isPicked && "line-through",
                    )}
                  >
                    {item.name}
                  </div>
                  <div className="mt-0.5 font-mono text-[10.5px] text-ink-50">
                    {formatQty(item.quantity)} {item.unit}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function formatQty(q: number): string {
  if (Number.isInteger(q)) return String(q);
  return q.toFixed(1).replace(".", ",");
}
